import { DOCUMENT } from '@angular/common';
import { ErrorHandler, Injectable, Injector, NgZone, Type } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ErrorDialogComponent } from '../components/error-dialog/error-dialog.component';
import { ConnectionLostError, IErrorLike, RequestTimeoutError, UnauthorizedError } from '../errors';
import { NotFoundError } from '../errors/NotFoundError';
import { ValidationError } from '../errors/ValidationError';
import { ERROR_TRANSFORMS, LOGGING_ERROR_HANDLERS, LOGGING_OPTIONS } from '../logging.constants';
import { IErrorHandle } from '../models/IErrorHandle';
import { IErrorHandler } from '../models/IErrorHandler';
import { IErrorTransform } from '../models/IErrorTransform';
import { ILoggingOptions } from '../models/ILoggingOptions';
import { LoggerService } from './logger.service';


@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
    private static errorPopupOpened = false;
    private _lastError: Error | IErrorLike = null;
    private _customHandlers: IErrorHandler[];
    private _customTransforms: IErrorTransform[];
    private _handlers: Map<Type<Error>, IErrorHandle<Error>>;
    private _options: ILoggingOptions;
    private _logger: LoggerService;
    private _router: Router;
    private _ngZone: NgZone;
    private _matDialog: MatDialog;
    private _matSnackBar: MatSnackBar;
    private _location: Location;

    connectionLost = false;

    constructor(
        private _injector: Injector,

    ) { }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    async handleError(originalError: Error | IErrorLike): Promise<void> {
        this._router ??= this._injector.get(Router);
        this._ngZone ??= this._injector.get(NgZone);
        this._matDialog ??= this._injector.get(MatDialog);
        this._matSnackBar ??= this._injector.get(MatSnackBar);
        this._location ??= this._injector.get(DOCUMENT).location;
        this._logger ??= this._injector.get(LoggerService);
        this._options ??= this._injector.get(LOGGING_OPTIONS);
        this._customHandlers ??= this._injector.get(LOGGING_ERROR_HANDLERS, []);
        this._customTransforms ??= this._injector.get(ERROR_TRANSFORMS, []);
        this._handlers ??= this.createHandlersMap();

        // stop to recieve other errors if client was disconnected from the server
        if (this.connectionLost) {
            return;
        }

        const errorInstance = this.buildError(originalError);
        if (errorInstance instanceof ConnectionLostError || errorInstance instanceof NotFoundError) {
            this.connectionLost = true;
        }

        const errorType = <Type<Error>>errorInstance.constructor;
        const handler = this._handlers.get(errorType) ?? this.commonHandler;
        this._logger.info('Error:\n', errorType);
        this._logger.info('Handler:\n', handler);
        await handler(errorInstance);
    }

    private createHandlersMap(): Map<Type<Error>, IErrorHandle<Error>> {
        const standard = new Map<Type<Error>, IErrorHandle<Error>>([
            [NotFoundError, this.connectionLostHandler],
            [ConnectionLostError, this.connectionLostHandler],
            [RequestTimeoutError, this.requestTimeoutHandler],
            [UnauthorizedError, this.unauthorizedErrorHandler],
            [ValidationError, this.validationErrorHandler],
        ]);
        this._customHandlers.forEach(ch => {
            standard.set(ch.type, ch.handle);
        });
        return standard;
    }

    private buildError(error: Error | IErrorLike): Error {

        error = this._customTransforms.reduce((err, transform) => transform(err), error);

        if (error.message.includes('Failed to fetch dynamically imported module')) {
            return new ConnectionLostError(error.message);
        }
        if (!(error instanceof Error)) {
            return new Error(error.message ?? JSON.stringify(error));
        }
        // Promise rejection wrapped by zone.js
        if (error['promise'] && error['rejection']) {
            return error['rejection'];
        }

        // Handle Client Error (Angular Error, ReferenceError...)
        return error;
    }

    private requestTimeoutHandler: IErrorHandle<RequestTimeoutError> = async error => {
        this._logger.warn(error);
        await this._logger.recordError(error);
        this._ngZone.run(() => {
            this._matDialog.closeAll();
            const dialogConfig: MatDialogConfig = {
                disableClose: true,
            };
            const dialogReference: MatDialogRef<ErrorDialogComponent> = this._matDialog.open(ErrorDialogComponent, dialogConfig);
            dialogReference.componentInstance.title = 'Notification';
            dialogReference.componentInstance.message = `Server is taking too long to respond.
Please check you Internet connection, reload the page and try again.`;
            dialogReference.componentInstance.choices = ['Reload'];
            dialogReference
                .afterClosed()
                .subscribe(() => {
                    location.reload();
                });
        });
    };

    private connectionLostHandler: IErrorHandle<ConnectionLostError> = async error => {
        this._logger.warn(error);
        await this._logger.recordWarning(error);
        this._ngZone.run(() => {
            this._matDialog.closeAll();
            const dialogConfig: MatDialogConfig = {
                disableClose: true,
            };
            const dialogReference: MatDialogRef<ErrorDialogComponent> = this._matDialog.open(ErrorDialogComponent, dialogConfig);
            dialogReference.componentInstance.title = 'Notification';
            dialogReference.componentInstance.message = `Connection to server has been lost.
Please check you Internet connection, reload the page and try again.`;
            if (!this._options.production) {
                dialogReference.componentInstance.message += `

[Dev Mode Message] Caution, this popup will also be displayed if any AJAX request will return 404`;
            }
            dialogReference.componentInstance.choices = ['Reload'];
            dialogReference
                .afterClosed()
                .subscribe(() => {
                    location.reload();
                });
        });
    };

    private unauthorizedErrorHandler: IErrorHandle<UnauthorizedError> = error => {
        this._logger.warn(error);
        this._ngZone.run(() => {
            this._matSnackBar.open('Access denied', 'OK', { duration: 4000 });
        });
        return Promise.resolve();
    };


    private validationErrorHandler: IErrorHandle<ValidationError> = error => {
        this._logger.warn(error);
        this._ngZone.run(() => {
            this._matSnackBar.open('Invalid operation', 'OK', { duration: 4000 });
        });
        return Promise.resolve();
    };

    private commonHandler: IErrorHandle<Error> = async error => {
        // do not spam same error logs over and over again
        if (this._lastError?.message != error.message) {
            this._logger.error(error);
            await this._logger.recordError(error);
        }
        this._lastError = error;

        if (this._options.errorPage) {
            this._location.href = this._options.errorPage;
            return;
        }
        if (GlobalErrorHandler.errorPopupOpened) {
            return;
        }
        GlobalErrorHandler.errorPopupOpened = true;
        this._logger.warn('To enable redirect to error set "errorPage" option');
        this._ngZone.run(() => {
            this._matDialog.closeAll();
            const dialogConfig: MatDialogConfig = {
                disableClose: true,
            };
            const dialogReference: MatDialogRef<ErrorDialogComponent> = this._matDialog.open(ErrorDialogComponent, dialogConfig);
            dialogReference.componentInstance.title = 'Error';
            dialogReference.componentInstance.message = 'Fatal Error! See console for details.';
            dialogReference.componentInstance.choices = ['Close', 'Reload'];
            dialogReference
                .afterClosed()
                .subscribe(response => {
                    if (response === 'Reload') {
                        location.reload();
                    }
                });
        });
    };
}


